/**
 * Free-tier watermark slotting -- the single source of truth for "which watermarks compete for
 * the same free slot".
 *
 * This mirrors iOS, which is the reference implementation for the shared template format. There,
 * `CommonMethods.removeExtraProFeaturesWatermarks` (applied whenever a template is applied, from
 * CanvasScreen / NewCanvasMenu / BulkCanvas) buckets by the watermark's type+subType:
 *
 *     case .eText:                                  -> one "text" slot   (covers text AND date:
 *                                                      both are type .eText on iOS)
 *     case .eImage: .eSignature                     -> one "signature" slot
 *     case .eImage: .eImage,.eSticker,.eEmoji,.eQRcode -> one shared "image" slot
 *
 * The important, non-obvious part is that last line: a sticker, emoji and QR code are NOT separate
 * types for limit purposes -- they share the single image slot. iOS enforces that at add time too
 * (stickers render as `ImageSubViewClass`, exactly what `hasImageWatermarkAlreadyAdded` counts
 * against `nWatermarkLimit = 1`).
 *
 * Web previously bucketed this way only when LOADING a template, while the add-time guards keyed
 * off the exact `property` string -- so a free user could add one sticker AND one image, save the
 * template, and then have whichever came second silently dropped on load. Its file had uploaded
 * fine and its data was intact in the template JSON; it simply never made it into the rendered
 * list. Keeping both sides on this one function is what stops that divergence coming back.
 */

/** Free users may have at most this many non-template watermarks at once. */
export const FREE_WATERMARK_LIMIT = 3;

/**
 * The free-tier slot a watermark `property` competes for. Watermarks sharing a slot are mutually
 * exclusive for free users, so compare slots -- never raw `property` -- in any limit check.
 *
 * `template` is not a user watermark and never occupies a slot; callers filter it out before
 * counting, and it maps to its own slot here so a stray one can't collide with anything.
 */
export const freeTierSlot = (property?: unknown): string => {
  const prop = String(property ?? "");
  if (prop === "template") return "template";
  if (prop === "signature") return "signature";
  if (prop === "text" || prop === "date") return "text";
  // image, sticker, emoji, qrcode and anything else image-like share one slot (iOS parity).
  return "image";
};
